'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faLink,
  faTimes,
  faArrowLeft,
  faChevronDown,
  faExclamationTriangle,
  faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import { Input, Button } from 'src/components';
import { PageHeader } from 'src/components/Settings/components';
import { Dialog } from 'src/components/PulseUI';
import {
  useCreateChainedStrategyMutation,
  useUpdateChainedStrategyMutation,
  useGetAllTargetStrategiesQuery,
  useGetAllEngageStrategiesQuery,
} from 'src/api/redux';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { StrategyTeamsDropdown } from 'src/components/Strategy/Admin/components';
import { NotificationToggle, SearchableStrategySelect } from './components';

interface Strategy {
  id: number;
  label: string;
  description: string;
  type?: string;
}

interface Team {
  id: number;
  name: string;
  description?: string;
}

interface ExistingChain {
  id: number;
  label: string;
  description: string;
  target_strategy?: Strategy;
  engage_strategies?: Strategy[];
  notifications_enabled?: boolean;
  teams?: (number | Team)[]; // Can be either array of IDs or array of team objects
}

interface CreateChainedStrategyRequest {
  id?: number;
  label: string;
  description: string;
  target_strategy?: number;
  engage_strategies: number[];
  teams: number[];
  notifications_enabled: boolean;
}

interface ApiError {
  data?: {
    error_message?: string;
  };
  message?: string;
}

interface ChainBuilderProps {
  onClose?: () => void;
  existingChain?: ExistingChain;
  isEditMode?: boolean;
  mode?: 'create' | 'edit' | 'copy';
}

interface ChainStep {
  id: string;
  type: 'target' | 'engage';
  strategyId: number | null;
  isDropdownOpen: boolean;
}

/**
 * ChainBuilder Component
 *
 * A form component for creating, editing, and copying strategy chains.
 * Strategy chains combine target strategies with engage/expand strategies
 * to create multi-step workflows.
 *
 * @example
 * // Create new chain
 * <ChainBuilder onClose={() => navigate('/chains')} />
 *
 * @example
 * // Edit existing chain
 * <ChainBuilder
 *   mode="edit"
 *   existingChain={chainData}
 *   onClose={() => navigate('/chains')}
 * />
 */
const ChainBuilder: React.FC<ChainBuilderProps> = ({
  onClose,
  existingChain,
  isEditMode = false,
  mode = 'create',
}) => {
  const router = useRouter();
  const log = useLogging();
  const { setNotification } = useNotification();
  const [createChainedStrategy, { isLoading: isCreating }] =
    useCreateChainedStrategyMutation();
  const [updateChainedStrategy, { isLoading: isUpdating }] =
    useUpdateChainedStrategyMutation();

  // Search state for each step
  const [targetSearch, setTargetSearch] = useState('');
  const [engageSearch, setEngageSearch] = useState('');

  // Fetch strategies with search
  const { data: targetStrategiesData, isLoading: isLoadingTarget } =
    useGetAllTargetStrategiesQuery({ q: targetSearch });
  const { data: engageStrategiesData, isLoading: isLoadingEngage } =
    useGetAllEngageStrategiesQuery({ q: engageSearch });

  const targetStrategies = targetStrategiesData?.results || [];
  const engageStrategies = engageStrategiesData?.results || [];

  // Determine the actual mode (support both mode prop and isEditMode for backwards compatibility)
  const actualMode = isEditMode ? 'edit' : mode;
  const isCopyMode = actualMode === 'copy';
  const isEdit = actualMode === 'edit';

  const [chainLabel, setChainLabel] = useState('');
  const [chainDescription, setChainDescription] = useState('');
  const [teams, setTeams] = useState<string[]>([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showNoTeamWarning, setShowNoTeamWarning] = useState(false);
  const [steps, setSteps] = useState<ChainStep[]>([
    { id: 'step-1', type: 'target', strategyId: null, isDropdownOpen: true },
  ]);

  // Initialize form with existing chain data in edit/copy mode
  useEffect(() => {
    if ((isEdit || isCopyMode) && existingChain) {
      // For copy mode, append "Copy" to the label
      setChainLabel(
        isCopyMode ? `${existingChain.label} (Copy)` : existingChain.label || ''
      );
      setChainDescription(existingChain.description || '');

      // Set teams from existing chain
      // Handle both team objects [{id, name, description}] and team IDs [1, 2, 3]
      // Convert to strings for internal state (teams API returns string IDs)
      const teamIds = existingChain.teams
        ? existingChain.teams.map((team: number | Team) =>
            typeof team === 'object' ? String(team.id) : String(team)
          )
        : [];
      setTeams(teamIds);
      setNotificationsEnabled(
        existingChain.notifications_enabled !== undefined
          ? existingChain.notifications_enabled
          : true
      );

      // Build steps from existing chain data
      const newSteps: ChainStep[] = [];

      // Add target strategy step if exists
      if (existingChain.target_strategy) {
        newSteps.push({
          id: 'step-1',
          type: 'target',
          strategyId: existingChain.target_strategy.id,
          isDropdownOpen: false,
        });
      }

      // Add engage strategy steps if they exist
      if (
        existingChain.engage_strategies &&
        existingChain.engage_strategies.length > 0
      ) {
        existingChain.engage_strategies.forEach((engageStrategy) => {
          newSteps.push({
            id: `step-${newSteps.length + 1}`,
            type: 'engage',
            strategyId: engageStrategy.id,
            isDropdownOpen: false,
          });
        });
      }

      if (newSteps.length > 0) {
        setSteps(newSteps);
      }
    }
  }, [isEdit, isCopyMode, existingChain]);

  const isSaving = isCreating || isUpdating;

  const handleAddStep = () => {
    // Only allow one target and one engage strategy for now
    if (steps.length < 2) {
      const newStep: ChainStep = {
        id: `step-${steps.length + 1}`,
        type: 'engage',
        strategyId: null,
        isDropdownOpen: true,
      };
      setSteps([...steps, newStep]);
    }
  };

  const toggleDropdown = (stepId: string) => {
    setSteps(
      steps.map((step) =>
        step.id === stepId
          ? { ...step, isDropdownOpen: !step.isDropdownOpen }
          : step
      )
    );

    // Focus the select after state update to open it
    setTimeout(() => {
      const selectElement = document.querySelector(
        `[data-step-id="${stepId}"] button[role="combobox"]`
      ) as HTMLElement;
      if (selectElement) {
        selectElement.click();
      }
    }, 0);
  };

  const handleRemoveStep = (stepId: string) => {
    if (steps.length > 1) {
      setSteps(steps.filter((step) => step.id !== stepId));
    }
  };

  const handleStrategySelect = (stepId: string, strategyId: number) => {
    setSteps(
      steps.map((step) =>
        step.id === stepId
          ? { ...step, strategyId, isDropdownOpen: false }
          : step
      )
    );
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      router.push('/settings/admin/strategy-manager/chain');
    }
  };

  /**
   * Handles the save button click with validation
   *
   * Validates the form state and shows a warning modal if no team is assigned
   * when creating a new chain. Prevents duplicate submissions during save.
   *
   * @returns Promise that resolves when save is complete or validation fails
   */
  const handleSave = async () => {
    // Prevent duplicate submissions
    if (isSaving) {
      return;
    }

    // Check if creating a new chain without a team
    if (!isEdit && (!teams || teams.length === 0)) {
      setShowNoTeamWarning(true);
      return;
    }

    await saveChain();
  };

  /**
   * Saves or updates a strategy chain
   *
   * Formats the chain data from the form state and calls the appropriate
   * API mutation (create or update). Handles success/error notifications
   * and logging. Navigates away on success.
   *
   * @throws {ApiError} When API request fails
   * @returns Promise that resolves when save operation completes
   */
  const saveChain = async () => {
    try {
      const action = isEdit ? 'update' : isCopyMode ? 'copy' : 'save';
      log.event(`Chain strategy ${action} initiated`, {
        source: 'ChainBuilder',
        chainLabel,
        stepsCount: steps.length,
        mode: actualMode,
      });

      // Format the chain data for the API
      let targetStrategyId: number | undefined;
      const engageStrategyIds: number[] = [];

      steps.forEach((step) => {
        if (step.strategyId) {
          if (step.type === 'target') {
            targetStrategyId = step.strategyId;
          } else if (step.type === 'engage') {
            engageStrategyIds.push(step.strategyId);
          }
        }
      });

      const chainData: CreateChainedStrategyRequest = {
        label: chainLabel,
        description: chainDescription,
        engage_strategies: engageStrategyIds, // Always include, even if empty array
        teams: teams.map((id) => Number(id)), // Convert string IDs to numbers for API
        notifications_enabled: notificationsEnabled,
      };

      if (targetStrategyId) {
        chainData.target_strategy = targetStrategyId;
      }

      if (isEdit && existingChain) {
        // Update existing chain
        chainData.id = existingChain.id;
        await updateChainedStrategy(chainData as Record<string, any> & { id: number }).unwrap();

        log.event('Chain strategy updated successfully', {
          source: 'ChainBuilder',
          chainLabel,
          chainId: existingChain.id,
        });

        setNotification({
          title: 'Chain Updated',
          message: `Strategy chain "${chainLabel}" has been updated successfully.`,
          type: 'success',
        });
      } else {
        // Create new chain (for both create and copy modes)
        await createChainedStrategy(chainData).unwrap();

        log.event(
          `Chain strategy ${isCopyMode ? 'copied' : 'created'} successfully`,
          {
            source: 'ChainBuilder',
            chainLabel,
            ...(isCopyMode &&
              existingChain && { copiedFromId: existingChain.id }),
          }
        );

        setNotification({
          title: isCopyMode ? 'Chain Copied' : 'Chain Created',
          message: `Strategy chain "${chainLabel}" has been ${
            isCopyMode ? 'copied' : 'created'
          } successfully.`,
          type: 'success',
        });
      }

      handleCancel();
    } catch (err: unknown) {
      const error = err as ApiError;
      const action = isEdit ? 'updating' : isCopyMode ? 'copying' : 'creating';
      log.exception(
        `Error ${action} chain strategy: ${
          error?.data?.error_message || error?.message || 'Unknown error'
        }`
      );

      setNotification({
        title: `Error ${
          isEdit ? 'Updating' : isCopyMode ? 'Copying' : 'Creating'
        } Chain`,
        message:
          error?.data?.error_message ||
          error?.message ||
          `An error occurred while ${action} the strategy chain.`,
        type: 'error',
      });
    }
  };

  /**
   * Retrieves the full strategy object for a given step
   *
   * Looks up the strategy from the appropriate list (target or engage)
   * based on the step type and strategyId.
   *
   * @param step - The chain step to get the strategy for
   * @returns The matching strategy object or null if not found
   */
  const getSelectedStrategy = (step: ChainStep) => {
    if (!step.strategyId) return null;
    if (step.type === 'target') {
      return targetStrategies.find((s) => s.id === step.strategyId);
    }
    return engageStrategies.find((s) => s.id === step.strategyId);
  };

  const getStrategyEditUrl = (step: ChainStep) => {
    const prefix = step.type === 'target' ? 'target' : 'engage';
    return `/settings/admin/strategy-manager/${prefix}/${step.strategyId}/edit`;
  };

  const canAddMoreSteps = steps.length < 2; // Only allow one target and one engage strategy for now
  const hasTargetStrategy = steps.some(
    (step) => step.type === 'target' && step.strategyId !== null
  );
  const isValid = chainLabel.trim() && hasTargetStrategy;

  return (
    <div className="flex flex-col gap-6 pb-64">
      <PageHeader className="bg-sky-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleCancel}
              variant="plain"
              color="neutral"
              size="sm"
              circular
            >
              <Icon icon={faArrowLeft} className="w-4 h-4" />
            </Button>
            <div className="flex flex-col">
              <h1 className="text-xl font-semibold text-neutral-700">
                {isEdit
                  ? 'Edit Strategy Chain'
                  : isCopyMode
                  ? 'Copy Strategy Chain'
                  : 'Create New Strategy Chain'}
              </h1>
              <p className="text-sm text-neutral">
                Build a multi-step strategy by combining target and
                engage/expand strategies
              </p>
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col gap-6">
          {/* Chain Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Chain Name and Description */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-primary text-sm font-semibold leading-tight">
                  Chain Label
                </label>
                <Input
                  type="text"
                  value={chainLabel}
                  onChange={(e) => setChainLabel(e.target.value)}
                  placeholder="Enter chain name"
                  fullWidth
                  variant="outlined"
                  size="md"
                  className="rounded-full"
                />
              </div>

              <div className="flex-1 flex flex-col gap-1">
                <label className="text-primary text-sm font-semibold leading-tight">
                  Description
                </label>
                <textarea
                  value={chainDescription}
                  onChange={(e) => setChainDescription(e.target.value)}
                  placeholder="Describe the purpose of this chain"
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-200 rounded-xl outline-none transition-all resize-none text-sm h-full"
                />
              </div>
            </div>

            {/* Right Column - Team Assignment */}
            <div className="flex flex-col gap-4">
              <StrategyTeamsDropdown
                selectedTeamIds={teams}
                onTeamSelection={(teamIds) => setTeams(teamIds)}
              />
              <div className="flex items-center justify-between gap-4 w-full ">
                <div className="flex flex-col flex-1 gap-1 whitespace-nowrap h-full">
                  <label className="text-primary text-sm font-semibold leading-tight">
                    Enable Notifications
                  </label>
                  <p className="text-xs text-secondary">
                    Notify team members about chain activity
                  </p>
                </div>
                <div className="flex flex-col flex-1">
                  <NotificationToggle
                    value={notificationsEnabled}
                    onChange={setNotificationsEnabled}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Chain Steps */}
          <div className="flex flex-col gap-4 pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-900">
                Chain Steps
              </div>
            </div>

            {/* Helper text */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
              <p className="text-sm text-sky-800">
                <strong>Note:</strong> You must choose at least one targeting
                strategy to build a chain.
              </p>
            </div>

            {/* Steps List */}
            <div className="flex flex-col">
              {steps.map((step, index) => {
                const selectedStrategy = getSelectedStrategy(step);
                // Show dropdown if no strategy selected, dropdown is open, or strategy not found
                const showDropdown =
                  !step.strategyId || step.isDropdownOpen || !selectedStrategy;

                return (
                  <div key={step.id} className="flex gap-3 relative">
                    {/* Step Number with connecting line */}
                    <div className="flex flex-col items-center relative">
                      <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-600 font-semibold flex items-center justify-center text-sm flex-shrink-0 z-10">
                        {index + 1}
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className="absolute w-0.5 bg-gray-300"
                          style={{
                            top: '40px',
                            bottom: '-16px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                          }}
                        />
                      )}
                    </div>

                    {/* Step Content */}
                    <div
                      className="flex-1 bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-4"
                      data-step-id={step.id}
                    >
                      {showDropdown ? (
                        <>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Icon
                                  icon={faLink}
                                  className="w-4 h-4 text-neutral-400"
                                />
                                <p className="text-sm font-medium text-neutral-700">
                                  {step.type === 'target'
                                    ? 'Target Strategy'
                                    : 'Engage/Expand Strategy'}
                                </p>
                              </div>
                              <p className="text-xs text-neutral-500">
                                {step.type === 'target'
                                  ? 'Select providers to target'
                                  : 'Define engage/expand approach'}
                              </p>
                            </div>
                            {index > 0 && (
                              <Button
                                onClick={() => handleRemoveStep(step.id)}
                                variant="plain"
                                color="danger"
                                size="sm"
                                circular
                                title="Remove step"
                              >
                                <Icon icon={faTimes} className="w-3 h-3" />
                              </Button>
                            )}
                          </div>

                          {/* Searchable Strategy Selector */}
                          <SearchableStrategySelect
                            value={step.strategyId}
                            onChange={(strategyId) =>
                              handleStrategySelect(step.id, strategyId)
                            }
                            strategies={
                              step.type === 'target'
                                ? targetStrategies
                                : engageStrategies
                            }
                            isLoading={
                              step.type === 'target'
                                ? isLoadingTarget
                                : isLoadingEngage
                            }
                            placeholder={`Select a ${step.type} strategy...`}
                            onSearchChange={
                              step.type === 'target'
                                ? setTargetSearch
                                : setEngageSearch
                            }
                            type={step.type}
                          />
                        </>
                      ) : (
                        /* Collapsed view with strategy card */
                        <div className="bg-white rounded-lg border border-neutral-200 p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Icon
                                  icon={faLink}
                                  className="w-4 h-4 text-neutral-400"
                                />
                                <p className="text-xs text-neutral-500">
                                  {step.type === 'target'
                                    ? 'Target Strategy'
                                    : 'Engage/Expand Strategy'}
                                </p>
                              </div>
                              <p className="text-base font-semibold text-neutral-800 mb-2">
                                {selectedStrategy?.label}
                              </p>
                              <p className="text-sm text-neutral-600">
                                {selectedStrategy?.description}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {step.strategyId && (
                                <Link
                                  href={getStrategyEditUrl(step)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                                  title={`Edit ${step.type} strategy`}
                                  aria-label={`Edit ${step.type} strategy: ${selectedStrategy?.label}`}
                                  data-testid={`edit-strategy-link-${step.id}`}
                                >
                                  <Icon
                                    icon={faPencil}
                                    className="w-4 h-4 text-neutral-600"
                                  />
                                </Link>
                              )}
                              {index > 0 && (
                                <Button
                                  onClick={() => handleRemoveStep(step.id)}
                                  variant="plain"
                                  color="danger"
                                  size="sm"
                                  circular
                                  title="Remove step"
                                >
                                  <Icon icon={faTimes} className="w-3 h-3" />
                                </Button>
                              )}
                              <button
                                onClick={() => toggleDropdown(step.id)}
                                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                                title="Change strategy"
                              >
                                <Icon
                                  icon={faChevronDown}
                                  className="w-4 h-4 text-neutral-600"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Step Button */}
            {canAddMoreSteps && (
              <Button
                onClick={handleAddStep}
                variant="outlined"
                color="primary"
                size="md"
                fullWidth
                startIcon={faPlus}
                className="border-2 border-dashed"
              >
                Add Engage/Expand Strategy
              </Button>
            )}

            {!canAddMoreSteps && steps.length >= 2 && (
              <div className="text-center py-2">
                <p className="text-sm text-neutral-500">
                  Maximum of 2 steps reached (1 target + 1 engage/expand)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 mt-6 border-t border-neutral-200 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleCancel}
              variant="plain"
              color="neutral"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isValid || isSaving}
              variant="solid"
              color="primary"
              size="md"
              loading={isSaving}
            >
              {isSaving
                ? isEdit
                  ? 'Updating...'
                  : isCopyMode
                  ? 'Copying...'
                  : 'Creating...'
                : isEdit
                ? 'Update Chain'
                : isCopyMode
                ? 'Copy Chain'
                : 'Create Chain'}
            </Button>
          </div>
        </div>
      </div>

      {/* No Team Warning Modal */}
      <Dialog
        isOpen={showNoTeamWarning}
        onClose={() => setShowNoTeamWarning(false)}
        size="md"
        className="h-auto"
      >
        <Dialog.Header>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full">
              <Icon
                icon={faExclamationTriangle}
                className="w-5 h-5 text-amber-600"
              />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              No Team Assigned
            </h2>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="p-6">
            <p className="text-slate-700 leading-relaxed">
              You have not assigned a team to this chain. Without a team
              assignment,{' '}
              <span className="font-semibold">
                no one will be able to view or access this chain
              </span>
              .
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Are you sure you want to continue without assigning a team?
            </p>
          </div>
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            onClick={() => setShowNoTeamWarning(false)}
            variant="plain"
            color="neutral"
            size="sm"
          >
            Go Back
          </Button>
          <Button
            onClick={() => {
              setShowNoTeamWarning(false);
              saveChain();
            }}
            variant="solid"
            color="warning"
            size="sm"
          >
            Continue Without Team
          </Button>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
};

export default ChainBuilder;
